/**
 * 分拣过程动画 —— 播放控制器
 *
 * 把 FramePlayer + SceneRenderer + 控制条（播放/暂停/单步/关键帧跳转/进度条/倍速）组装到给定容器。
 * 订阅播放器快照驱动渲染：
 *   - play / 单步 / 关键帧跳转 / 自动推进 tick → FLIP 过渡（flip=true）
 *   - 进度条 seek / 段落点击 → 瞬时 snap（flip=false），并交由渲染层终止进行中过渡
 * 快捷键：Space / Enter → 播放/暂停（焦点在输入控件上时不拦截）。
 * 进度条上方分阶段：分组 / 合并·第1轮…第n轮 / 最终，可点击跳转。
 */

import type { FrameProvider } from './types';
import { FramePlayer } from './player';
import { SceneRenderer } from './renderer';

export interface ProcessPlayerHandle {
  /** 释放播放器（清理定时器与订阅） */
  dispose(): void;
}

const SPEEDS = [0.5, 1, 2];

interface Segment {
  label: string;
  start: number;
  end: number;
}

/** 把帧序列划分为阶段段落：分组 / 合并各轮 / 最终（直接读 provider 的 wire 元数据，不物化帧） */
function buildSegments(provider: FrameProvider): Segment[] {
  const segs: Segment[] = [];
  const n = provider.length;
  if (n === 0) return segs;

  // 分组段：raw / expanded / recommend
  let g = 0;
  while (g < n && provider.phaseAt(g) !== 'merge' && provider.phaseAt(g) !== 'final') g++;
  if (g > 0) segs.push({ label: '分组', start: 0, end: g - 1 });

  // 合并段：按 merge-round-end 切分为各轮
  if (g < n && provider.phaseAt(g) === 'merge') {
    const start = g;
    let k = g;
    while (k < n && provider.phaseAt(k) === 'merge') k++;
    const last = k - 1;
    let hasRounds = false;
    for (let i = start; i <= last; i++) {
      if (provider.actionAt(i) === 'merge-round-end') {
        hasRounds = true;
        break;
      }
    }
    if (!hasRounds) {
      segs.push({ label: '合并', start, end: last });
    } else {
      let round = 0;
      let roundStart = start;
      for (let i = start; i <= last; i++) {
        if (provider.actionAt(i) === 'merge-round-end') {
          round++;
          segs.push({ label: `合并·第${round}轮`, start: roundStart, end: i });
          roundStart = i + 1;
        }
      }
      if (roundStart <= last) segs.push({ label: round === 0 ? '合并' : '合并·收尾', start: roundStart, end: last });
    }
    g = k;
  }

  // 最终段
  if (g < n && provider.phaseAt(g) === 'final') segs.push({ label: '最终', start: g, end: n - 1 });
  return segs;
}

/** 找到 t 所属段落 */
function segmentAt(segs: Segment[], t: number): number {
  for (let i = 0; i < segs.length; i++) {
    if (t >= segs[i].start && t <= segs[i].end) return i;
  }
  return -1;
}

/**
 * 在 root 内挂载过程动画播放器。
 * @param root 挂载容器
 * @param provider 帧提供者（Reconstructor 等）；length===0 视为「过程数据无效」
 */
export function mountProcessPlayer(root: HTMLElement, provider: FrameProvider): ProcessPlayerHandle {
  root.innerHTML = `
    <div class="anim-scene"></div>
    <div class="anim-controls">
      <button class="anim-btn" data-act="prev" title="上一帧（单步后退）">‹</button>
      <button class="anim-btn" data-act="prevKey" title="上一关键帧">⏮</button>
      <button class="anim-btn anim-btn-primary" data-act="play" title="播放/暂停（空格/回车）">▶</button>
      <button class="anim-btn" data-act="nextKey" title="下一关键帧">⏭</button>
      <button class="anim-btn" data-act="next" title="下一帧（单步前进）">›</button>
      <div class="anim-seek-wrap">
        <div class="anim-segments" data-el="segments"></div>
        <input class="anim-seek" type="range" data-act="seek" min="0" max="0" value="0" step="1">
      </div>
      <span class="anim-counter" data-el="counter">0 / 0</span>
      <select class="anim-speed" data-act="speed" title="倍速">
        ${SPEEDS.map(s => `<option value="${s}">${s}x</option>`).join('')}
      </select>
    </div>
    <div class="anim-error" data-el="error" hidden>过程数据无效</div>
  `;

  const sceneEl = root.querySelector('.anim-scene') as HTMLElement;
  const renderer = new SceneRenderer(sceneEl);
  const player = new FramePlayer();

  const $ = <T extends HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const prevBtn = $<HTMLButtonElement>('[data-act="prev"]');
  const prevKeyBtn = $<HTMLButtonElement>('[data-act="prevKey"]');
  const playBtn = $<HTMLButtonElement>('[data-act="play"]');
  const nextKeyBtn = $<HTMLButtonElement>('[data-act="nextKey"]');
  const nextBtn = $<HTMLButtonElement>('[data-act="next"]');
  const seekInput = $<HTMLInputElement>('[data-act="seek"]');
  const speedSelect = $<HTMLSelectElement>('[data-act="speed"]');
  const counterEl = $<HTMLElement>('[data-el="counter"]');
  const errorEl = $<HTMLElement>('[data-el="error"]');
  const segmentsEl = $<HTMLElement>('[data-el="segments"]');

  const valid = provider.length > 0;
  const segs = valid ? buildSegments(provider) : [];
  // 渲染阶段段落（按帧数比例分宽）
  segmentsEl.innerHTML = segs.map(s => `
    <div class="anim-seg" data-start="${s.start}" data-end="${s.end}" style="flex-grow:${Math.max(1, s.end - s.start + 1)}" title="${s.label}（帧 ${s.start + 1}-${s.end + 1}）"><span>${s.label}</span></div>
  `).join('');

  // 是否以 snap（非 FLIP）渲染下一次快照；仅 seek 与初始加载置 true
  let snapNext = true;

  const unsubscribe = player.subscribe(snap => {
    const frame = player.current();
    if (frame) renderer.render(frame, { flip: !snapNext });
    snapNext = false;

    playBtn.textContent = snap.playing ? '⏸' : '▶';
    playBtn.disabled = !snap.valid;
    prevBtn.disabled = !snap.valid || snap.atStart;
    nextBtn.disabled = !snap.valid || snap.atEnd;
    prevKeyBtn.disabled = !snap.valid || player.prevKeyFrameIndex() === null;
    nextKeyBtn.disabled = !snap.valid || player.nextKeyFrameIndex() === null;
    seekInput.disabled = !snap.valid;
    seekInput.max = String(Math.max(0, snap.total - 1));
    if (document.activeElement !== seekInput) seekInput.value = String(snap.t);
    counterEl.textContent = snap.valid ? `${snap.t + 1} / ${snap.total}` : '0 / 0';
    errorEl.hidden = snap.valid;

    // 高亮当前阶段段落
    const activeIdx = snap.valid ? segmentAt(segs, snap.t) : -1;
    Array.from(segmentsEl.children).forEach((el, i) => {
      el.classList.toggle('active', i === activeIdx);
    });
  });

  prevBtn.addEventListener('click', () => { snapNext = false; player.prev(); });
  prevKeyBtn.addEventListener('click', () => { snapNext = false; player.prevKeyFrame(); });
  nextKeyBtn.addEventListener('click', () => { snapNext = false; player.nextKeyFrame(); });
  nextBtn.addEventListener('click', () => { snapNext = false; player.next(); });
  playBtn.addEventListener('click', () => { snapNext = false; player.toggle(); });
  speedSelect.addEventListener('change', () => {
    snapNext = false;
    player.setSpeed(Number(speedSelect.value));
  });
  seekInput.addEventListener('input', () => {
    snapNext = true;
    player.seek(Number(seekInput.value));
  });
  // 点击阶段段落 → 跳到该段起始帧
  segmentsEl.addEventListener('click', e => {
    const seg = (e.target as HTMLElement).closest('[data-start]') as HTMLElement | null;
    if (!seg) return;
    snapNext = true;
    player.seek(Number(seg.dataset.start));
  });

  // 快捷键：Space / Enter → 播放/暂停（焦点在输入控件/按钮上时不拦截，避免双触发与误操作）
  const onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Space' && e.key !== 'Enter' && e.key !== ' ') return;
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
    e.preventDefault();
    snapNext = false;
    player.toggle();
  };
  document.addEventListener('keydown', onKey);

  // 初始加载：snap 渲染第 0 帧
  snapNext = true;
  const ok = player.loadProvider(provider);
  if (!ok) {
    // 非法数据：渲染层显示空场景 + 错误提示（订阅已置 errorEl.hidden=false）
    renderer.clear();
  }

  return {
    dispose() {
      document.removeEventListener('keydown', onKey);
      unsubscribe();
      player.dispose();
      root.innerHTML = '';
    },
  };
}
